"use client";
import { getUserInfo } from "@/http/user";
import { useEffect, useMemo } from "react";
import * as React from "react";
import { useChatStore } from "@/store/use-chat-store";
import { useSession } from "next-auth/react";
import type { Session } from "next-auth";

import type { User } from "@/types/user";
import { userLogin, postOauthToken } from "@/http/auth";
import { useAuthExpiredStore } from "./auth-expired-store";
import dayjs from "dayjs";

export interface UserContextValue {
  user: User | null;
  isLoading: boolean;
  accessYears: [number, number];
  refreshUser: () => void;

  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => void;
}

export const UserContext = React.createContext<UserContextValue | undefined>(undefined);

export interface UserProviderProps {
  children: React.ReactNode;
}
declare module "next-auth" {
  interface Session {
    googleToken?: string;
  }
}

export function UserProvider({ children }: UserProviderProps): React.JSX.Element {
  const { setExpired } = useAuthExpiredStore();

  const { data: session } = useSession();

  const { setUIState } = useChatStore();

  const [user, setUser] = React.useState<User | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const accessYears: [number, number] = useMemo(() => {
    const end = dayjs().year() - 1;
    const start = end - (user?.fiscal_years || 0) + 1;
    return [start, end];
  }, [user]);

  const refreshUser = async () => {
    try {
      const userFromServer = await getUserInfo();
      setUser(userFromServer);
    } catch (err) {
      console.error("refresh user failed", err);
    }
  };

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const user: User = await getUserInfo();
        setUser(user);
        setIsLoading(false);
      } catch (error) {
        signOut();
        setIsLoading(false);
      }
    };
    initializeAuth();
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      const user: User = await userLogin(email, password);
      document.cookie = `token=${user.token}; path=/; max-age=${30 * 24 * 3600}; SameSite=Strict; Secure`;
      setUser(user);
      setExpired(false);
    } catch (error) {
      setUser(null);
      setExpired(true);
      throw error;
    }
  };

  const onPostOauthToken = async (token: string) => {
    try {
      const user: User = await postOauthToken(token, "google");
      document.cookie = `token=${user.token}; path=/; max-age=${30 * 24 * 3600}; SameSite=Strict; Secure`;
      setUser(user);
      setExpired(false);
    } catch (error) {
      setUser(null);
      setExpired(true);
      throw error;
    }
  };
  useEffect(() => {
    if ((session as any)?.googleToken && !user?.token) {
      if (session?.googleToken) {
        onPostOauthToken(session.googleToken);
      }
    }
  }, [session]);

  const signOut = () => {
    setUIState({
      isInitialized: false,
    });
    coreSignOut();
    setUser(null);
    setExpired(true);
  };

  return (
    <UserContext.Provider
      value={{
        user,
        isLoading,
        accessYears,
        refreshUser,
        signIn,
        signOut,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

export const coreSignOut = () => {
  document.cookie = `token=; path=/; `;
};
